import type { GridStackWidget } from 'gridstack';
import type { DashboardWidgetContent, WidgetOptions } from '../Components/Dashboard/dashboardTypes';
import type { DriveUnitPortOccupancy } from '../redux/ApplicationSlice';
import {
  BOOLEAN_WIDGET_TYPE,
  isElectronicSensorStreamSensorKey,
  isMotorStreamSensorKey,
  isOtherElectronicsStreamSensorKey,
  MECHANISM_WIDGET_TYPE,
  MOTOR_MULTI_WIDGET_TYPE,
  MOTOR_WIDGET_TYPE,
  motorMultiWidgetDataKey,
  otherClassNameFromStreamKey,
  parseMotorMultiWidgetDataKey,
  sensorClassNameFromStreamKey,
  streamPortFromSensorKey,
  OTHER_WIDGET_TYPE,
  SENSOR_WIDGET_TYPE,
} from '../Components/Graphs/sensor-utils';
import { COUNTDOWN_TIMER_WIDGET_TYPE } from './countdownTimerCodeUtils';
import {
  buildMechanismVariableNameByPort,
  buildMotorVariableNameByPort,
  buildOtherElectronicsVariableNameByPort,
  buildSensorVariableNameByPort,
  getOtherPickerEntries,
} from './electronicsDashboardUtils';
import { historicLayoutWidgetPorts, parseDashboardWidgetContent } from './sensor-stream-tracker';
import { stableJsonSignature } from './stableJsonSignature';

export const DEFAULT_WIDGET_CONTENT_CURVE = 'linear';
export const DEFAULT_WIDGET_CONTENT_STYLE = 'line';

const SNAPSHOT_ONLY_WIDGET_OPTION_KEYS = new Set<keyof WidgetOptions>([
  'portDisplayLabelsByPort',
  'motorPortDisplayLabelsByPort',
  'motorPortDisplayLabel',
]);

/** Prefix for newly created dashboard widget ids (legacy widgets may use `widget-`). */
export const DASHBOARD_WIDGET_ID_PREFIX = 'w-';

/** Creates a unique dashboard widget id for new widgets dropped onto the grid. */
export function createDashboardWidgetId(): string {
  return `${DASHBOARD_WIDGET_ID_PREFIX}${Math.random().toString(36).substring(2, 15)}`;
}

export const DEFAULT_WIDGET_MIN_W = 2;
export const DEFAULT_WIDGET_MIN_H = 2;
/** Minimum height for compact widgets (timer, countdown, single-boolean). */
export const COMPACT_WIDGET_MIN_H = 1;
export const DEFAULT_WIDGET_MAX_W = 6;
export const DEFAULT_WIDGET_MAX_H = 6;
/** Wider max width for line-chart widgets (e.g. multi-axis motor charts). */
export const CHART_WIDGET_MAX_W = 10;
/** Minimum size for multi-axis motor/mechanism charts. */
export const MOTOR_MULTI_WIDGET_MIN_W = 4;
export const MOTOR_MULTI_WIDGET_MIN_H = 4;

function isCompactDashboardWidgetType(type: string): boolean {
  return type === 'timer' || type === COUNTDOWN_TIMER_WIDGET_TYPE || type === BOOLEAN_WIDGET_TYPE;
}

/**
 * Multi-boolean widgets keep the default 2-row minimum; only single-boolean can be 2x1.
 *
 * All-ports widgets stay multi even while one port is configured, so adding a port later
 * does not force the placed widget to grow.
 */
function isMultiPortBooleanWidgetContent(content: DashboardWidgetContent): boolean {
  if (content.name !== BOOLEAN_WIDGET_TYPE) return false;
  const options = content.options;
  if (options?.allMotorPorts || options?.allSensorPorts || options?.allOtherPorts) return true;
  return historicLayoutWidgetPorts(content).length > 1;
}

/**
 * Widget types that render as line charts via `GraphWidget` and benefit from a wider max width.
 * Mirrors the `showGraphWidget` branch in `WidgetBase`.
 */
export function isLineChartWidgetType(type: string, dataKey?: string): boolean {
  if (!type) return false;
  if (type === 'pos' && dataKey === 'all') return false;
  if (type === 'speed') return false;
  if (type === BOOLEAN_WIDGET_TYPE) return false;
  if (type === COUNTDOWN_TIMER_WIDGET_TYPE) return false;
  if (type === 'orientation' || type === 'field' || type === 'console' || type === 'timer') return false;
  return true;
}

export function isMotorMultiWidgetType(type: string): boolean {
  return type === MOTOR_MULTI_WIDGET_TYPE;
}

export function resolveWidgetMinSize(type: string): { minW: number; minH: number } {
  if (isMotorMultiWidgetType(type)) {
    return { minW: MOTOR_MULTI_WIDGET_MIN_W, minH: MOTOR_MULTI_WIDGET_MIN_H };
  }
  if (isCompactDashboardWidgetType(type)) {
    return { minW: DEFAULT_WIDGET_MIN_W, minH: COMPACT_WIDGET_MIN_H };
  }
  return { minW: DEFAULT_WIDGET_MIN_W, minH: DEFAULT_WIDGET_MIN_H };
}

/** Combines drawer metadata with per-type minimums (e.g. motor_multi 4x4). */
export function resolveEffectiveWidgetMinSize(type: string, drawerMinW?: number, drawerMinH?: number): { minW: number; minH: number } {
  const typeMinSize = resolveWidgetMinSize(type);
  return {
    minW: Math.max(drawerMinW ?? DEFAULT_WIDGET_MIN_W, typeMinSize.minW),
    minH: Math.max(drawerMinH ?? DEFAULT_WIDGET_MIN_H, typeMinSize.minH),
  };
}

export function resolveWidgetMaxSize(type: string, dataKey?: string): { maxW: number; maxH: number } {
  if (isLineChartWidgetType(type, dataKey)) {
    return { maxW: CHART_WIDGET_MAX_W, maxH: DEFAULT_WIDGET_MAX_H };
  }
  return { maxW: DEFAULT_WIDGET_MAX_W, maxH: DEFAULT_WIDGET_MAX_H };
}

export function resolveWidgetMinSizeFromContent(content: string | undefined): { minW: number; minH: number } {
  const parsed = parseDashboardWidgetContent(content);
  if (!parsed) {
    return { minW: DEFAULT_WIDGET_MIN_W, minH: DEFAULT_WIDGET_MIN_H };
  }
  if (isMultiPortBooleanWidgetContent(parsed)) {
    return { minW: DEFAULT_WIDGET_MIN_W, minH: DEFAULT_WIDGET_MIN_H };
  }
  return resolveWidgetMinSize(parsed.name);
}

export function resolveWidgetMaxSizeFromContent(content: string | undefined): { maxW: number; maxH: number } {
  const parsed = parseDashboardWidgetContent(content);
  if (!parsed) {
    return { maxW: DEFAULT_WIDGET_MAX_W, maxH: DEFAULT_WIDGET_MAX_H };
  }
  return resolveWidgetMaxSize(parsed.name, parsed.dataKey);
}

export function resolveWidgetSizeConstraints(
  type: string,
  dataKey?: string,
  drawerMinW?: number,
  drawerMinH?: number
): { minW: number; minH: number; maxW: number; maxH: number } {
  return {
    ...resolveEffectiveWidgetMinSize(type, drawerMinW, drawerMinH),
    ...resolveWidgetMaxSize(type, dataKey),
  };
}

function parseDrawerMinDimension(value: unknown): number | undefined {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

/** Resolves drag-preview / placement min size from drawer metadata and widget type. */
export function resolveDragWidgetMinSize(type: string | undefined, drawerMinW: unknown, drawerMinH: unknown): { minW: number; minH: number } {
  const parsedMinW = parseDrawerMinDimension(drawerMinW);
  const parsedMinH = parseDrawerMinDimension(drawerMinH);
  if (type) {
    return resolveEffectiveWidgetMinSize(type, parsedMinW, parsedMinH);
  }
  return {
    minW: parsedMinW ?? DEFAULT_WIDGET_MIN_W,
    minH: parsedMinH ?? DEFAULT_WIDGET_MIN_H,
  };
}

/** Fields stored in `.bps` project files and on-device project payloads. */
export type PersistableDashboardWidget = Pick<GridStackWidget, 'id' | 'x' | 'y' | 'w' | 'h' | 'content'>;

export type PersistableDashboardWidgetContent = Pick<DashboardWidgetContent, 'name' | 'dataKey'> &
  Partial<Pick<DashboardWidgetContent, 'curve' | 'style' | 'sensorKeys' | 'options'>>;

/** Live project context used to omit widget variable names that program code can resolve. */
export type PersistableDashboardContext = {
  driveUnitPorts: DriveUnitPortOccupancy[];
  projectCode: string;
};

/** Builds persistence context when program code and drive-unit ports are available. */
export function buildPersistableDashboardContext(
  driveUnitPorts: DriveUnitPortOccupancy[],
  projectCode: string
): PersistableDashboardContext | undefined {
  const trimmedCode = projectCode.trim();
  if (!trimmedCode || !driveUnitPorts.length) return undefined;
  return { driveUnitPorts, projectCode: trimmedCode };
}

function variableNameIsDerivableFromProgram(variableNameByPort: ReadonlyMap<number, string>, port: number): boolean {
  return Boolean(variableNameByPort.get(port)?.trim());
}

function widgetVariableNameOptionKeysToOmit(content: DashboardWidgetContent, context: PersistableDashboardContext): Set<keyof WidgetOptions> {
  const keysToOmit = new Set<keyof WidgetOptions>();
  if (!context.projectCode.trim() || !context.driveUnitPorts.length) {
    return keysToOmit;
  }

  const { driveUnitPorts, projectCode } = context;
  const opts = content.options ?? {};
  const ports = historicLayoutWidgetPorts(content);

  if (content.name === MOTOR_MULTI_WIDGET_TYPE && opts.selectedMotorPort != null) {
    const port = opts.selectedMotorPort;
    const prefix = parseMotorMultiWidgetDataKey(content.dataKey)?.prefix;
    const isMechanism = prefix === 'mechanism' || Boolean(opts.mechanismClassName);
    const variableNameByPort = isMechanism
      ? buildMechanismVariableNameByPort(driveUnitPorts, projectCode)
      : buildMotorVariableNameByPort(driveUnitPorts, projectCode);
    if (variableNameIsDerivableFromProgram(variableNameByPort, port)) {
      keysToOmit.add('motorVariableName');
    }
    return keysToOmit;
  }

  const isMechanismWidget = content.name === MECHANISM_WIDGET_TYPE || content.sensorKeys.some((key) => key.startsWith('mechanism.'));
  const isMotorWidget =
    content.name === MOTOR_WIDGET_TYPE || (content.name === BOOLEAN_WIDGET_TYPE && content.sensorKeys.some(isMotorStreamSensorKey));

  if (isMotorWidget || isMechanismWidget) {
    if (ports.length === 1) {
      const port = ports[0];
      const variableNameByPort = isMechanismWidget
        ? buildMechanismVariableNameByPort(driveUnitPorts, projectCode)
        : buildMotorVariableNameByPort(driveUnitPorts, projectCode);
      if (variableNameIsDerivableFromProgram(variableNameByPort, port)) {
        keysToOmit.add('motorVariableName');
      }
    }
    return keysToOmit;
  }

  const isSensorWidget =
    content.name === SENSOR_WIDGET_TYPE || (content.name === BOOLEAN_WIDGET_TYPE && content.sensorKeys.some(isElectronicSensorStreamSensorKey));

  if (isSensorWidget && ports.length === 1) {
    const sensorClassName = opts.sensorClassName ?? sensorClassNameFromStreamKey(content.dataKey);
    if (sensorClassName) {
      const variableNameByPort = buildSensorVariableNameByPort(driveUnitPorts, projectCode, sensorClassName);
      if (variableNameIsDerivableFromProgram(variableNameByPort, ports[0])) {
        keysToOmit.add('sensorVariableName');
      }
    }
    return keysToOmit;
  }

  const isOtherWidget = content.name === OTHER_WIDGET_TYPE || content.sensorKeys.some(isOtherElectronicsStreamSensorKey);

  if (isOtherWidget && ports.length === 1) {
    const port = ports[0];
    const otherClassName = opts.otherClassName ?? otherClassNameFromStreamKey(content.dataKey);
    const variableNameByPort = otherClassName
      ? new Map(getOtherPickerEntries(driveUnitPorts, projectCode, otherClassName).map((entry) => [entry.port, entry.variableName]))
      : buildOtherElectronicsVariableNameByPort(driveUnitPorts, projectCode);
    if (variableNameIsDerivableFromProgram(variableNameByPort, port)) {
      keysToOmit.add('otherVariableName');
      if (opts.motorVariableName?.trim() && !opts.otherVariableName?.trim()) {
        keysToOmit.add('motorVariableName');
      }
    }
  }

  return keysToOmit;
}

function toPersistableWidgetOptions(
  options?: WidgetOptions,
  content?: DashboardWidgetContent,
  persistContext?: PersistableDashboardContext
): WidgetOptions | undefined {
  if (!options) return undefined;
  const omitKeys = new Set(SNAPSHOT_ONLY_WIDGET_OPTION_KEYS);
  if (content && persistContext) {
    for (const key of widgetVariableNameOptionKeysToOmit(content, persistContext)) {
      omitKeys.add(key);
    }
  }
  const entries = Object.entries(options).filter(([key]) => !omitKeys.has(key as keyof WidgetOptions));
  if (!entries.length) return undefined;
  return Object.fromEntries(entries) as WidgetOptions;
}

function resolvedSensorKeysMatchPersistableCandidate(content: DashboardWidgetContent, candidate: PersistableDashboardWidgetContent): boolean {
  const rehydrated = parseDashboardWidgetContent(JSON.stringify(candidate));
  if (!rehydrated) return false;
  return stableJsonSignature(content.sensorKeys) === stableJsonSignature(rehydrated.sensorKeys);
}

/** Strips redundant widget `content` JSON before project persistence. */
export function toPersistableDashboardWidgetContentObject(
  content: DashboardWidgetContent,
  persistContext?: PersistableDashboardContext
): PersistableDashboardWidgetContent {
  const persistable: PersistableDashboardWidgetContent = {
    name: content.name,
    dataKey: content.dataKey,
  };

  if (content.curve !== DEFAULT_WIDGET_CONTENT_CURVE) {
    persistable.curve = content.curve;
  }
  if (content.style !== DEFAULT_WIDGET_CONTENT_STYLE) {
    persistable.style = content.style;
  }

  const options = toPersistableWidgetOptions(content.options, content, persistContext);
  if (options) {
    persistable.options = options;
  }

  if (!resolvedSensorKeysMatchPersistableCandidate(content, persistable)) {
    persistable.sensorKeys = content.sensorKeys;
  }

  return persistable;
}

/** Compacts serialized widget `content` for project files; returns the input when unparseable. */
export function toPersistableDashboardWidgetContent(content: string | undefined, persistContext?: PersistableDashboardContext): string | undefined {
  if (!content) return content;
  const parsed = parseDashboardWidgetContent(content);
  if (!parsed) return content;
  return JSON.stringify(toPersistableDashboardWidgetContentObject(parsed, persistContext));
}

/** Strips GridStack runtime-only fields (min/max sizes, etc.) before persisting a widget. */
export function toPersistableDashboardWidget(widget: GridStackWidget, persistContext?: PersistableDashboardContext): PersistableDashboardWidget {
  const persistable: PersistableDashboardWidget = { id: widget.id };

  if (widget.x !== undefined) persistable.x = widget.x;
  if (widget.y !== undefined) persistable.y = widget.y;
  if (widget.w !== undefined) persistable.w = widget.w;
  if (widget.h !== undefined) persistable.h = widget.h;
  if (widget.content !== undefined) {
    persistable.content = toPersistableDashboardWidgetContent(widget.content, persistContext);
  }

  return persistable;
}

/** Strips runtime-only widget fields from a dashboard layout before project persistence. */
export function toPersistableDashboardLayout(layout: GridStackWidget[], persistContext?: PersistableDashboardContext): PersistableDashboardWidget[] {
  return layout.map((widget) => toPersistableDashboardWidget(widget, persistContext));
}

export type SaveDashboardLayoutPayload =
  | GridStackWidget[]
  | {
      layout: GridStackWidget[];
      persistContext?: PersistableDashboardContext;
    };

/** Normalizes `onSaveLayout` payloads that may include live persistence context. */
export function resolveSaveDashboardLayoutPayload(payload: SaveDashboardLayoutPayload): {
  layout: GridStackWidget[];
  persistContext?: PersistableDashboardContext;
} {
  return Array.isArray(payload) ? { layout: payload } : payload;
}

/** Applies min/max size constraints when loading or creating dashboard widgets. */
export function normalizeWidgetSizeConstraints(widget: GridStackWidget): GridStackWidget {
  const { minW: typeMinW, minH: typeMinH } = resolveWidgetMinSizeFromContent(widget.content);
  const { maxW: typeMaxW, maxH: typeMaxH } = resolveWidgetMaxSizeFromContent(widget.content);
  const minW = Math.max(widget.minW ?? typeMinW, typeMinW);
  const minH = Math.max(widget.minH ?? typeMinH, typeMinH);

  return {
    ...widget,
    minW,
    minH,
    maxW: Math.max(widget.maxW ?? typeMaxW, typeMaxW),
    maxH: Math.max(widget.maxH ?? typeMaxH, typeMaxH),
    w: Math.max(widget.w || minW, minW),
    h: Math.max(widget.h || minH, minH),
  };
}

export function remapStreamPortInSensorKey(sensorKey: string, newPort: number): string {
  return sensorKey.replace(/\.port\d+\./, `.port${newPort}.`);
}

export function remapDriveUnitPortInStreamKey(sensorKey: string, oldPort: number, newPort: number): string {
  const parsedMulti = parseMotorMultiWidgetDataKey(sensorKey);
  if (parsedMulti && parsedMulti.port === oldPort) {
    return motorMultiWidgetDataKey(newPort, parsedMulti.prefix);
  }

  const usesPort = isMotorStreamSensorKey(sensorKey) || isElectronicSensorStreamSensorKey(sensorKey) || isOtherElectronicsStreamSensorKey(sensorKey);
  return usesPort && streamPortFromSensorKey(sensorKey) === oldPort ? remapStreamPortInSensorKey(sensorKey, newPort) : sensorKey;
}

function remapSelectedPorts(ports: number[] | undefined, oldPort: number, newPort: number): number[] | undefined {
  if (!ports?.length) return ports;
  return [...new Set(ports.map((port) => (port === oldPort ? newPort : port)))].sort((a, b) => a - b);
}

/** Remaps stream keys and selected drive-unit ports when a motor or sensor port changes. */
export function remapDriveUnitPortInWidgetContent(content: DashboardWidgetContent, oldPort: number, newPort: number): DashboardWidgetContent {
  const dataKey = remapDriveUnitPortInStreamKey(content.dataKey, oldPort, newPort);
  const sensorKeys = (content.sensorKeys ?? []).map((key) => remapDriveUnitPortInStreamKey(key, oldPort, newPort));

  const selectedMotorPorts = remapSelectedPorts(content.options?.selectedMotorPorts, oldPort, newPort);
  const selectedMotorPort = content.options?.selectedMotorPort === oldPort ? newPort : content.options?.selectedMotorPort;
  const selectedSensorPorts = remapSelectedPorts(content.options?.selectedSensorPorts, oldPort, newPort);
  const selectedOtherPorts = remapSelectedPorts(content.options?.selectedOtherPorts, oldPort, newPort);

  if (
    selectedMotorPorts === content.options?.selectedMotorPorts &&
    selectedMotorPort === content.options?.selectedMotorPort &&
    selectedSensorPorts === content.options?.selectedSensorPorts &&
    selectedOtherPorts === content.options?.selectedOtherPorts
  ) {
    return { ...content, dataKey, sensorKeys };
  }

  return {
    ...content,
    dataKey,
    sensorKeys,
    options: {
      ...content.options,
      ...(selectedMotorPorts !== undefined ? { selectedMotorPorts } : {}),
      ...(selectedMotorPort !== undefined ? { selectedMotorPort } : {}),
      ...(selectedSensorPorts !== undefined ? { selectedSensorPorts } : {}),
      ...(selectedOtherPorts !== undefined ? { selectedOtherPorts } : {}),
    },
  };
}
